import React from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Theme,
  Box,
  Checkbox,
  Chip,
  Link,
  Tooltip,
} from "@mui/material";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import { StyledTableRow } from "../../../../../../styles/Shipmentstyles";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import TableState from "../../../../../../views/components/common/table-states/TableState";
import { useStores } from "../../../../use-stores";
import { observer } from "mobx-react";
import { ProjectSourcingProduct } from "../../../../../../models/ProjectManagement/ProjectSourcingProduct";
import { isPdfUrl } from "../../../../../../utils/urlHelper";

interface SourcingOpsTableProps {
  maxHeight?: number;
  minHeight?: number;
  archived?: number;
  handleNavigateById: (id: number) => void;
}

const SourcingOpsTable: React.FC<SourcingOpsTableProps> = ({
  minHeight = window.innerHeight - 350,
  handleNavigateById,
  archived,
}) => {
  const theme = useTheme<Theme>();
  const { projectSourcingOpsStore } = useStores();

  return (
    <>
      <TableContainer sx={{ minHeight: minHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <AppTableHeaderCell
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  minWidth: "40px",
                }}
              >
                <Checkbox
                  sx={{
                    borderRadius: "4px",
                    color: "rgba(139, 139, 139, 1)",
                    "& .MuiSvgIcon-root": { fontSize: 25 },
                  }}
                  onClick={projectSourcingOpsStore.handleSelectAllProductsForPr}
                  checked={
                    projectSourcingOpsStore.products.length > 0 &&
                    (projectSourcingOpsStore.purchaseRequestProducts?.length ??
                      0) === projectSourcingOpsStore.products.length
                  }
                  disabled={archived === 1}
                />
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="0.75rem"
                fontSize="0.75rem"
                fontWeight={700}
                fontColor={theme.palette.customColors.blackBodyBackgorund}
                sx={{
                  paddingLeft: 0,
                }}
              >
                <Box
                  sx={{
                    whiteSpace: "pre-line",
                    textAlign: "left",
                    lineHeight: "1.2rem",
                  }}
                >
                  Product{"\n"}Name
                </Box>
              </AppTableHeaderCell>
              <AppTableHeaderCell
                minWidth="0.75rem"
                fontSize="0.75rem"
                fontWeight={700}
                fontColor={theme.palette.customColors.blackBodyBackgorund}
              >
                <Box sx={{ whiteSpace: "pre-line", lineHeight: "1.2rem" }}>
                  Customization{"\n"}Required
                </Box>
              </AppTableHeaderCell>
              {projectSourcingOpsStore.tasks.map((item, index) => (
                <AppTableHeaderCell
                  key={index}
                  minWidth="0.75rem"
                  fontSize="0.75rem"
                  fontWeight={700}
                  fontColor={theme.palette.customColors.blackBodyBackgorund}
                >
                  <Box sx={{ whiteSpace: "pre-line", lineHeight: "1.2rem" }}>
                    {(() => {
                      const words = item.task
                        .split("_")
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() +
                            word.slice(1).toLowerCase(),
                        );

                      return words.length === 3
                        ? `${words[0]} ${words[1]}\n${words[2]}`
                        : words.join("\n");
                    })()}
                  </Box>
                </AppTableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {projectSourcingOpsStore?.products?.map(
              (item: ProjectSourcingProduct) => {
                const thumbUrl = item?.mockups?.[0]?.image_url
                  ? item.mockups[0].image_url
                  : (item?.image_url ??
                    "https://procureasystorage.s3.ap-south-1.amazonaws.com/logos/SELogoSmall.png");

                return (
                  <StyledTableRow key={item.id}>
                    <AppTableRowCell
                      sx={{
                        borderBottom: "none !important",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Checkbox
                        sx={{
                          borderRadius: "4px",
                          paddingBottom: "1rem",
                          color: "rgba(139, 139, 139, 1)",
                          "& .MuiSvgIcon-root": { fontSize: 25 },
                        }}
                        onClick={() =>
                          projectSourcingOpsStore.handleSingleSelectProductForPr(
                            item,
                          )
                        }
                        checked={projectSourcingOpsStore.purchaseRequestProducts?.some(
                          (selectedProduct) => selectedProduct?.id === item?.id,
                        )}
                        disabled={archived === 1}
                      />
                    </AppTableRowCell>
                    <AppTableRowCell
                      fontSize="1rem"
                      fontWeight={500}
                      textAlign="left"
                      sx={{
                        borderBottom: "none !important",
                        paddingLeft: 0,
                        paddingRight: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "start",
                          gap: "1.781rem",
                        }}
                      >
                        <Box
                          sx={{
                            width: "2.875rem",
                            height: "2.875rem",
                            flexShrink: 0,
                            borderRadius: "4px",
                            overflow: "hidden",
                            bgcolor: theme.palette.customColors.lightGreyColor,
                          }}
                        >
                          {isPdfUrl(thumbUrl) ? (
                            <Box
                              component="iframe"
                              src={thumbUrl}
                              title={item?.name || "PDF preview"}
                              sx={{
                                width: "100%",
                                height: "100%",
                                border: 0,
                                display: "block",
                                pointerEvents: "none",
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={thumbUrl}
                              alt={item?.name || "Default Image"}
                              sx={{
                                maxHeight: "2.875rem",
                                maxWidth: "2.875rem",
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          )}
                        </Box>
                        <Box
                          sx={{
                            textAlign: "left",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {/* <Box
                            sx={{
                              whiteSpace: "pre-line",
                              cursor: "pointer",
                            }}
                            onClick={() => handleNavigateById(item.id)}
                          ></Box> */}
                          <CustomTypography
                            variant="body1"
                            fontWeight={500}
                            color={
                              theme.palette.customColors.blackBodyBackgorund
                            }
                            style={{ cursor: "pointer" }}
                            onClick={() => handleNavigateById(item.id)}
                          >
                            {item.name}
                          </CustomTypography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              label={`Qty: ${item.quantity}`}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                backgroundColor: theme.palette.blue.main + "18",
                                color: theme.palette.blue.main,
                                borderRadius: "4px",
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                            {item.id ? (
                              <Link
                                href={`/products/${item.id}`}
                                target="_blank"
                                underline="none"
                                style={{ padding: 0 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Chip
                                  label={`#${item.id}`}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    backgroundColor:
                                      theme.palette.purple.main + "30",
                                    color: theme.palette.purple.main,
                                    borderRadius: "4px",
                                    "& .MuiChip-label": { px: 0.75 },
                                  }}
                                />
                              </Link>
                            ) : null}
                            {item.has_psd_templates === 1 ? (
                              <Tooltip title="PSD templates configured">
                                <Chip
                                  label="PSD"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    backgroundColor:
                                      theme.palette.red.dark + "30",
                                    color: theme.palette.red.dark,
                                    borderRadius: "4px",
                                    "& .MuiChip-label": { px: 0.75 },
                                  }}
                                />
                              </Tooltip>
                            ) : null}
                          </Box>
                        </Box>
                      </Box>
                    </AppTableRowCell>
                    <AppTableRowCell
                      sx={{
                        textAlign: "center",
                        position: "relative",
                        padding: 0,
                        borderBottom: "none !important",
                        width: "12%",
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        position="relative"
                        width="100%"
                      >
                        <Box
                          sx={{
                            zIndex: 1,
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            backgroundColor:
                              theme.palette.customColors.grayBackGroundColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <TableState
                            state={item.customisation_required ? true : false}
                          />
                        </Box>

                        <Box
                          sx={{
                            position: "absolute",
                            width: "50%",
                            height: "2px",
                            backgroundColor:
                              theme.palette.customColors.grayBackGroundColor,
                            right: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 0,
                          }}
                        />
                      </Box>
                    </AppTableRowCell>

                    {item?.tasks?.map((key, idx, arr) => {
                      return (
                        <AppTableRowCell
                          key={idx}
                          sx={{
                            textAlign: "center",
                            position: "relative",
                            padding: 0,
                            borderBottom: "none !important",
                            width: "12%",
                          }}
                        >
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            position="relative"
                            width="100%"
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                width: "50%",
                                height: "2px",
                                backgroundColor:
                                  theme.palette.customColors
                                    .grayBackGroundColor,
                                left: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 0,
                              }}
                            />

                            <Box
                              sx={{
                                zIndex: 1,
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                backgroundColor:
                                  theme.palette.customColors
                                    .grayBackGroundColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <TableState
                                state={
                                  key.task === "pr_created"
                                    ? (item.pr_quantity ?? 0) >= item.quantity
                                    : key.task === "po_created"
                                      ? (item.po_quantity ?? 0) >= item.quantity
                                      : key.active === 1
                                }
                                qty={
                                  key.task === "pr_created" &&
                                  (item.pr_quantity ?? 0) < item.quantity
                                    ? item.pr_quantity
                                    : key.task === "po_created" &&
                                        (item.po_quantity ?? 0) < item.quantity
                                      ? item.po_quantity
                                      : undefined
                                }
                              />
                            </Box>

                            {key.task === "pr_created" && (
                              <Box
                                sx={{
                                  fontSize: "0.7rem",
                                  color: theme.palette.text.primary,
                                  textAlign: "center",
                                  whiteSpace: "nowrap",
                                  marginTop: "5px",
                                  position: "absolute",
                                  bottom: "-25px",
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                }}
                              >
                                <CustomTypography
                                  variant="body4"
                                  fontWeight={500}
                                >
                                  {item.vendor_names || ""} <br />
                                </CustomTypography>
                              </Box>
                            )}

                            {idx < arr.length - 1 && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  width: "50%",
                                  height: "2px",
                                  backgroundColor:
                                    theme.palette.customColors
                                      .grayBackGroundColor,
                                  right: 0,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  zIndex: 0,
                                }}
                              />
                            )}
                          </Box>
                        </AppTableRowCell>
                      );
                    })}
                  </StyledTableRow>
                );
              },
            )}
          </TableBody>
        </Table>

        {projectSourcingOpsStore.products.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: 15,
            }}
          >
            <CustomTypography
              color={theme.palette.customColors.greyTextColor}
              variant="body2"
              fontWeight={700}
            >
              NO PRODUCTS FOUND!
            </CustomTypography>
          </Box>
        )}
      </TableContainer>
    </>
  );
};

export default observer(SourcingOpsTable);
